import { Component, OnInit, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { PerformanceService } from '../../core/performance.service';

@Component({
  selector: 'app-performance-monitor',
  template: '',
  standalone: true
})
export class PerformanceMonitorComponent implements OnInit {
  private platformId = inject(PLATFORM_ID);
  private performanceService = inject(PerformanceService);
  private isBrowser = isPlatformBrowser(this.platformId);

  ngOnInit() {
    if (!this.isBrowser) return;
    
    this.initializePerformanceMonitoring();
  }

  private initializePerformanceMonitoring() {
    // Monitor Core Web Vitals
    this.monitorCoreWebVitals();
    
    // Monitor resource loading
    this.monitorResourceLoading();
    
    // Monitor user interactions
    this.monitorUserInteractions();
  }

  private monitorCoreWebVitals() {
    // Largest Contentful Paint (LCP)
    new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      const lastEntry = entries[entries.length - 1];
      console.log('LCP:', lastEntry.startTime);
      
      if (lastEntry.startTime > 2500) {
        console.warn('LCP is too slow:', lastEntry.startTime);
      }
    }).observe({ entryTypes: ['largest-contentful-paint'] });

    // First Input Delay (FID)
    new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      entries.forEach((entry: any) => {
        console.log('FID:', entry.processingStart - entry.startTime);
        
        if (entry.processingStart - entry.startTime > 100) {
          console.warn('FID is too slow:', entry.processingStart - entry.startTime);
        }
      });
    }).observe({ entryTypes: ['first-input'] });

    // Cumulative Layout Shift (CLS)
    new PerformanceObserver((entryList) => {
      let clsValue = 0;
      const entries = entryList.getEntries();
      
      entries.forEach((entry: any) => {
        if (!entry.hadRecentInput) {
          clsValue += entry.value;
        }
      });
      
      console.log('CLS:', clsValue);
      
      if (clsValue > 0.1) {
        console.warn('CLS is too high:', clsValue);
      }
    }).observe({ entryTypes: ['layout-shift'] });
  }

  private monitorResourceLoading() {
    // Monitor image loading performance
    const images = document.querySelectorAll('img');
    images.forEach(img => {
      img.addEventListener('load', () => {
        const loadTime = performance.now();
        console.log('Image loaded:', img.src, 'in', loadTime, 'ms');
      });
      
      img.addEventListener('error', () => {
        console.error('Image failed to load:', img.src);
      });
    });
  }

  private monitorUserInteractions() {
    // Monitor scroll performance
    const throttledScroll = this.performanceService.throttle(() => {
      // Track scroll performance
    }, 16); // ~60fps

    window.addEventListener('scroll', throttledScroll);

    // Monitor click performance
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      if (target.tagName === 'BUTTON' || target.tagName === 'A') {
        console.log('User interaction:', target.tagName, target.textContent);
      }
    });
  }

  // Public method to get performance metrics
  getPerformanceMetrics() {
    if (!this.isBrowser) return null;

    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    const paint = performance.getEntriesByType('paint');
    
    return {
      // Navigation timing
      domContentLoaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
      loadComplete: navigation.loadEventEnd - navigation.loadEventStart,
      domInteractive: navigation.domInteractive - navigation.fetchStart,
      
      // Paint timing
      firstPaint: paint.find(entry => entry.name === 'first-paint')?.startTime,
      firstContentfulPaint: paint.find(entry => entry.name === 'first-contentful-paint')?.startTime,
      
      // Memory usage
      memory: (performance as any).memory ? {
        usedJSHeapSize: (performance as any).memory.usedJSHeapSize,
        totalJSHeapSize: (performance as any).memory.totalJSHeapSize,
        jsHeapSizeLimit: (performance as any).memory.jsHeapSizeLimit
      } : null,
      
      // Connection info
      connection: (navigator as any).connection ? {
        effectiveType: (navigator as any).connection.effectiveType,
        downlink: (navigator as any).connection.downlink,
        rtt: (navigator as any).connection.rtt
      } : null
    };
  }
} 